import { useCallback, useEffect, useRef, useState } from "react";
import { Square, MoveUpRight, Pencil, Undo2, Eraser, Check, X as XIcon } from "lucide-react";

/* ──────────────────────────────────────────────────────────────────────
   ImageAnnotator — draw boxes, arrows and freehand marks on an image so the
   agent sees exactly what the operator points at (Agent 3).

   Marks are stored in IMAGE pixels, drawn on a display canvas scaled to fit,
   and burned into a full-resolution JPEG on Done. Only the marked-up image is
   sent to the model — the marks are the message.
   ────────────────────────────────────────────────────────────────────── */

type Tool = "box" | "arrow" | "pen";
type Pt = { x: number; y: number };
type Mark = { tool: Tool; color: string; pts: Pt[] };

const COLORS: string[] = ["#e11d48", "#f59e0b", "#2563eb", "#16a34a"];

function drawMark(g: CanvasRenderingContext2D, m: Mark, lw: number) {
  g.strokeStyle = m.color; g.fillStyle = m.color; g.lineWidth = lw; g.lineCap = "round"; g.lineJoin = "round";
  const [a, b] = [m.pts[0], m.pts[m.pts.length - 1]];
  if (!a || !b) return;
  if (m.tool === "box") {
    g.strokeRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
  } else if (m.tool === "arrow") {
    g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y); g.stroke();
    const ang = Math.atan2(b.y - a.y, b.x - a.x), head = lw * 5;
    g.beginPath(); g.moveTo(b.x, b.y);
    g.lineTo(b.x - head * Math.cos(ang - 0.45), b.y - head * Math.sin(ang - 0.45));
    g.lineTo(b.x - head * Math.cos(ang + 0.45), b.y - head * Math.sin(ang + 0.45));
    g.closePath(); g.fill();
  } else {
    g.beginPath(); g.moveTo(a.x, a.y);
    for (const p of m.pts.slice(1)) g.lineTo(p.x, p.y);
    g.stroke();
  }
}

export function ImageAnnotator({ src, onDone, onCancel }: {
  src: string; onDone: (dataUrl: string) => void; onCancel: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [tool, setTool] = useState<Tool>("box");
  const [color, setColor] = useState<string>(COLORS[0] ?? "#e11d48");
  const [marks, setMarks] = useState<Mark[]>([]);
  const drawing = useRef<Mark | null>(null);
  const [, force] = useState(0);

  useEffect(() => { const i = new Image(); i.onload = () => setImg(i); i.src = src; }, [src]);

  // line width in IMAGE pixels: visible on a 1856-wide screenshot and on a 600-wide crop alike
  const lw = img ? Math.max(3, Math.round(Math.max(img.naturalWidth, img.naturalHeight) / 320)) : 4;

  const redraw = useCallback(() => {
    const c = canvasRef.current;
    if (!c || !img) return;
    const g = c.getContext("2d")!;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, c.width, c.height);
    const k = c.width / img.naturalWidth;
    g.setTransform(k, 0, 0, k, 0, 0);
    g.drawImage(img, 0, 0);
    for (const m of marks) drawMark(g, m, lw);
    if (drawing.current) drawMark(g, drawing.current, lw);
  }, [img, marks, lw]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !img) return;
    const maxW = window.innerWidth * 0.9, maxH = window.innerHeight * 0.74;
    const k = Math.min(1, maxW / img.naturalWidth, maxH / img.naturalHeight);
    c.width = Math.round(img.naturalWidth * k); c.height = Math.round(img.naturalHeight * k);
    redraw();
  }, [img, redraw]);

  const toImage = (e: React.PointerEvent<HTMLCanvasElement>): Pt => {
    const c = canvasRef.current!, r = c.getBoundingClientRect();
    const k = img!.naturalWidth / r.width;
    return { x: (e.clientX - r.left) * k, y: (e.clientY - r.top) * k };
  };

  const down = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!img) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = toImage(e);
    drawing.current = { tool, color, pts: [p, p] };
    force((n) => n + 1);
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const d = drawing.current;
    if (!d) return;
    const p = toImage(e);
    if (d.tool === "pen") d.pts.push(p); else d.pts[1] = p;
    redraw();
  };
  const up = () => {
    const d = drawing.current;
    drawing.current = null;
    if (!d) return;
    const [a, b] = [d.pts[0], d.pts[d.pts.length - 1]];
    if (!a || !b) return;
    if (d.tool !== "pen" && Math.hypot(b.x - a.x, b.y - a.y) < lw * 2) { redraw(); return; }   // a click, not a mark
    setMarks((m) => [...m, d]);
  };

  const done = () => {
    if (!img) return;
    const c = document.createElement("canvas");
    c.width = img.naturalWidth; c.height = img.naturalHeight;
    const g = c.getContext("2d")!;
    g.drawImage(img, 0, 0);
    for (const m of marks) drawMark(g, m, lw);
    onDone(c.toDataURL("image/jpeg", 0.88));
  };

  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
      if ((e.metaKey || e.ctrlKey) && e.key === "z") { e.preventDefault(); setMarks((m) => m.slice(0, -1)); }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onCancel]);

  const ToolBtn = ({ t, label, Icon }: { t: Tool; label: string; Icon: typeof Square }) => (
    <button type="button" onClick={() => setTool(t)} title={label}
            className="np-btn-ghost px-2 py-1 border flex items-center gap-1 font-mono text-[11px]"
            style={{ borderColor: tool === t ? "var(--accent)" : "var(--rule)", color: tool === t ? "var(--accent)" : "var(--fg)",
                     background: tool === t ? "var(--accent-soft)" : "transparent" }}>
      <Icon size={13} /> {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-2 p-4"
         style={{ background: "rgba(10,10,10,0.78)" }} role="dialog" aria-label="Mark up image">
      <div className="np-card flex items-center gap-2 px-3 py-2 flex-wrap">
        <ToolBtn t="box" label="Box" Icon={Square} />
        <ToolBtn t="arrow" label="Arrow" Icon={MoveUpRight} />
        <ToolBtn t="pen" label="Pen" Icon={Pencil} />
        <span className="mx-1 h-5 w-px" style={{ background: "var(--rule)" }} />
        {COLORS.map((c) => (
          <button key={c} type="button" onClick={() => setColor(c)} aria-label={`colour ${c}`}
                  className="w-5 h-5 rounded-full" style={{ background: c, outline: color === c ? "2px solid var(--fg)" : "none", outlineOffset: 2 }} />
        ))}
        <span className="mx-1 h-5 w-px" style={{ background: "var(--rule)" }} />
        <button type="button" className="np-btn-ghost px-2 py-1 border flex items-center gap-1 font-mono text-[11px]" style={{ borderColor: "var(--rule)" }}
                disabled={!marks.length} onClick={() => setMarks((m) => m.slice(0, -1))} title="Undo (⌘Z)"><Undo2 size={13} /> Undo</button>
        <button type="button" className="np-btn-ghost px-2 py-1 border flex items-center gap-1 font-mono text-[11px]" style={{ borderColor: "var(--rule)" }}
                disabled={!marks.length} onClick={() => setMarks([])}><Eraser size={13} /> Clear</button>
        <span className="font-mono text-[10px] text-[var(--fg-faint)] ml-2">{marks.length} mark{marks.length === 1 ? "" : "s"} · drag on the image</span>
        <span className="flex-1" />
        <button type="button" className="np-btn-ghost px-2 py-1 border flex items-center gap-1 font-mono text-[11px]" style={{ borderColor: "var(--rule)" }} onClick={onCancel}><XIcon size={13} /> Cancel</button>
        <button type="button" className="np-btn px-3 py-1 flex items-center gap-1 font-mono text-[11px]" onClick={done}><Check size={13} /> Done</button>
      </div>
      {img ? (
        <canvas ref={canvasRef} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
                style={{ cursor: "crosshair", touchAction: "none", boxShadow: "0 6px 30px rgba(0,0,0,.5)", background: "#fff" }} />
      ) : <div className="font-mono text-[11px] text-white">loading image…</div>}
    </div>
  );
}
